import { forwardRef, type ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/cn';
import { buttonVariants, type ButtonProps } from './button';
import { Spinner } from './spinner';

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /**
   * Required, and becomes the `aria-label`. An icon-only control with no label
   * is a mystery box to a screen reader; making this mandatory is the only
   * reliable way to stop that shipping (CONTRACT §8, UI rule 5).
   */
  label: string;
  icon: LucideIcon;
  variant?: ButtonProps['variant'];
  /** Icon buttons are square. `sm` is 36px and is desktop-only. */
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  /** Also show the label as a `title` tooltip. Off by default; prefer <Tooltip>. */
  showTitle?: boolean;
}

const SIZE_CLASS = {
  sm: 'size-9 [&_svg]:size-4',
  md: 'size-11 [&_svg]:size-5',
  lg: 'size-13 [&_svg]:size-6',
} as const;

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      label,
      icon: Icon,
      variant = 'ghost',
      size = 'md',
      loading = false,
      showTitle = false,
      className,
      disabled,
      type,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        aria-label={label}
        title={showTitle ? label : undefined}
        aria-busy={loading || undefined}
        disabled={disabled || loading}
        className={cn(
          buttonVariants({ variant, size: 'icon' }),
          SIZE_CLASS[size],
          'p-0',
          className,
        )}
        {...props}
      >
        {loading ? (
          <Spinner size="sm" label={label} />
        ) : (
          <Icon aria-hidden="true" focusable="false" />
        )}
      </button>
    );
  },
);
